import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface SiteContent {
  id: string;
  section: string;
  title: string | null;
  subtitle: string | null;
  description: string | null;
  metadata: Record<string, any>;
  isActive: boolean;
}

export const useSiteContent = (section: string) => {
  const [content, setContent] = useState<SiteContent | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchContent = async () => {
    try {
      setIsLoading(true);
      const { data, error: fetchError } = await supabase
        .from('site_content')
        .select('*')
        .eq('section', section)
        .eq('is_active', true)
        .maybeSingle();

      if (fetchError) throw fetchError;

      if (data) {
        setContent({
          id: data.id,
          section: data.section,
          title: data.title,
          subtitle: data.subtitle,
          description: data.description,
          metadata: (data.metadata as Record<string, any>) || {},
          isActive: data.is_active,
        });
      } else {
        // No active content row found for this section; let the UI use defaults
        setContent(null);
      }
      setError(null);
    } catch (err) {
      console.error(`Error fetching content for section ${section}:`, err);
      setError('Failed to load content');
      setContent(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, [section]);

  return { content, isLoading, error, refetch: fetchContent };
};
